import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  InputAdornment,
  MenuItem
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Search } from '@mui/icons-material';
import { locationAPI, companyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const ENTITY_TYPES = [
  'IT',
  'Logistics'
];

const Locations = () => {
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    name: '',
    entity: '',
    company: '',
    city: '',
    state: '',
    country: 'USA'
  });
  const { isAdmin } = useAuth();

  useEffect(() => {
    fetchLocations();
    fetchCompanies();
  }, [searchTerm]);

  useEffect(() => {
    // Filter companies based on selected entity
    if (formData.entity) {
      const filtered = companies.filter(c => c.entity === formData.entity);
      setFilteredCompanies(filtered);
      // Reset company selection if current company doesn't match entity
      if (formData.company) {
        const currentCompany = companies.find(c => c._id === formData.company);
        if (currentCompany && currentCompany.entity !== formData.entity) {
          setFormData(prev => ({ ...prev, company: '' }));
        }
      }
    } else {
      setFilteredCompanies(companies);
    }
  }, [formData.entity, companies]);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const response = await locationAPI.getAll({ search: searchTerm, limit: 100 });
      setLocations(response.data.data);
    } catch (error) {
      showSnackbar('Failed to fetch locations', 'error');
    }
    setLoading(false);
  };

  const fetchCompanies = async () => {
    try {
      const response = await companyAPI.getAll({ isActive: 'true' });
      setCompanies(response.data.data);
    } catch (error) {
      console.error('Failed to fetch companies', error);
    }
  };

  const handleOpenDialog = (location = null) => {
    if (location) {
      setEditMode(true);
      setCurrentLocation(location);
      setFormData({
        name: location.name,
        entity: location.entity || '',
        company: location.company?._id || location.company || '',
        city: location.city || '',
        state: location.state || '',
        country: location.country || 'USA'
      });
    } else {
      setEditMode(false);
      setCurrentLocation(null);
      setFormData({
        name: '',
        entity: '',
        company: '',
        city: '',
        state: '',
        country: 'USA'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      name: '',
      entity: '',
      company: '',
      city: '',
      state: '',
      country: 'USA'
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await locationAPI.update(currentLocation._id, formData);
        showSnackbar('Location updated successfully', 'success');
      } else {
        await locationAPI.create(formData);
        showSnackbar('Location created successfully', 'success');
      }
      handleCloseDialog();
      fetchLocations();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Operation failed', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        await locationAPI.delete(id);
        showSnackbar('Location deleted successfully', 'success');
        fetchLocations();
      } catch (error) {
        showSnackbar(error.response?.data?.message || 'Delete failed', 'error');
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const columns = [
    { 
      field: 'name', 
      headerName: 'Location Name', 
      flex: 1, 
      minWidth: 150,
      valueGetter: (params) => params.row.name || 'N/A'
    },
    { 
      field: 'entity', 
      headerName: 'Entity', 
      flex: 1, 
      minWidth: 130,
      renderCell: (params) => (
        params.value ? (
          <Chip
            label={params.value}
            color="primary"
            variant="outlined"
            size="small"
          />
        ) : (
          'N/A'
        )
      )
    },
    { 
      field: 'company', 
      headerName: 'Company', 
      flex: 1, 
      minWidth: 150,
      valueGetter: (params) => params.row.company?.name || 'N/A'
    },
    { 
      field: 'city', 
      headerName: 'City', 
      flex: 1, 
      minWidth: 120,
      valueGetter: (params) => params.row.city || 'N/A'
    },
    { 
      field: 'state', 
      headerName: 'State', 
      flex: 1, 
      minWidth: 120,
      valueGetter: (params) => params.row.state || 'N/A'
    },
    { 
      field: 'country', 
      headerName: 'Country', 
      flex: 1, 
      minWidth: 100,
      valueGetter: (params) => params.row.country || 'N/A'
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          {isAdmin && (
            <>
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleOpenDialog(params.row)}
              >
                <Edit />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDelete(params.row._id)}
              >
                <Delete />
              </IconButton>
            </>
          )}
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Locations</Typography>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add Location
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search locations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            )
          }}
        />
      </Paper>

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={locations}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          loading={loading}
          getRowId={(row) => row._id}
          disableSelectionOnClick
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode ? 'Edit Location' : 'Add New Location'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label="Location Name"
              type="text"
              fullWidth
              required
              value={formData.name}
              onChange={handleChange}
            />
            <TextField
              select
              margin="dense"
              name="entity"
              label="Entity Type"
              fullWidth
              required
              value={formData.entity}
              onChange={handleChange}
            >
              {ENTITY_TYPES.map((entity) => (
                <MenuItem key={entity} value={entity}>
                  {entity}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              margin="dense"
              name="company"
              label="Company (Optional)"
              fullWidth
              value={formData.company}
              onChange={handleChange}
              disabled={!formData.entity}
              helperText={!formData.entity ? 'Please select an entity first' : 'Leave empty to post directly under entity'}
            >
              <MenuItem value="">
                <em>None - Post under Entity only</em>
              </MenuItem>
              {filteredCompanies.map((company) => (
                <MenuItem key={company._id} value={company._id}>
                  {company.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              margin="dense"
              name="city"
              label="City"
              type="text"
              fullWidth
              value={formData.city}
              onChange={handleChange}
            />
            <TextField
              margin="dense"
              name="state"
              label="State"
              type="text"
              fullWidth
              value={formData.state}
              onChange={handleChange}
            />
            <TextField
              margin="dense"
              name="country"
              label="Country"
              type="text"
              fullWidth
              value={formData.country}
              onChange={handleChange}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editMode ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Locations;
